// « Déjà vu » : pannes déjà résolues dans l'entreprise sur le même équipement, la même référence ou le même code défaut.
// Chaque résultat dit POURQUOI il remonte (niveau de correspondance), qui a confirmé la cause et quand ; aucun pourcentage.
// « Reprendre comme hypothèse » ajoute une HYPOTHÈSE à vérifier, jamais une cause confirmée.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CloudOff, FileText, History, Lightbulb } from 'lucide-react';
import { api, messageErreur, type ErreurApi } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, pluriel } from '../../lib/format.ts';
import { Badge, Bouton, Chargement, EtatErreur, LienBouton, Panneau, useNotifier } from '../../components/ui.tsx';
import type { Intervention, PanneResolue, ResultatDejaVu } from './types.ts';

const VISIBLES = 3;

function BadgeNiveau({ p }: { p: PanneResolue }) {
  if (p.pertinence_faible) return <Badge ton="neutre" icone={false}>{p.niveau_libelle} — pertinence faible</Badge>;
  return <Badge ton={p.niveau === 'meme_code_fabricant' ? 'neutre' : 'accent'} icone={false}>{p.niveau_libelle}</Badge>;
}

/** Liste partagée (intervention et fiche équipement). `reprendre` absent = consultation seule. */
export function ListePannesResolues({ pannes, reprendre, occupe }: {
  pannes: PanneResolue[]; reprendre?: (p: PanneResolue, causeId: string) => void; occupe?: string | null;
}) {
  const [tout, setTout] = useState(false);
  const visibles = tout ? pannes : pannes.slice(0, VISIBLES);
  return (
    <>
      <ul className="int-dejavu">
        {visibles.map((p) => (
          <li key={p.id} className="int-dejavu-element">
            <div className="rangee int-dejavu-entete">
              <BadgeNiveau p={p} />
              {p.codes_communs.length > 0 && <span className="petit secondaire">code <span className="mono">{p.codes_communs.join(', ')}</span></span>}
            </div>
            <p className="petit secondaire">
              Cause confirmée{p.auteur ? <> par <strong className="int-dejavu-auteur">{p.auteur}</strong></> : ''} le {date(p.date)}
              {' '}— <span className="mono int-dejavu-ref">{p.reference}</span>
            </p>
            <ul className="int-dejavu-causes">
              {p.causes.map((c) => (
                <li key={c.id}>
                  <p className="int-dejavu-cause">{c.texte}</p>
                  {reprendre && (c.deja_reprise
                    ? <span className="petit discret">Déjà reprise comme hypothèse.</span>
                    : <div><Bouton compact icone={<Lightbulb aria-hidden />} chargement={occupe === `${p.id}:${c.id}`} disabled={!!occupe}
                        aria-label={`Reprendre comme hypothèse : ${c.texte.slice(0, 80)}`}
                        onClick={() => reprendre(p, c.id)}>Reprendre comme hypothèse</Bouton></div>)}
                </li>
              ))}
            </ul>
            {p.operations.length > 0 && <p className="petit"><span className="secondaire">Opérations : </span>{p.operations.join(' ; ')}</p>}
            {p.mesures.length > 0 && (
              <p className="petit expert-seulement"><span className="secondaire">Mesures : </span>
                {p.mesures.map((x, k) => <span key={k}>{k ? ' ; ' : ''}{x.point} <span className="mono">{x.valeur} {x.unite}</span></span>)}</p>
            )}
            <p className="tres-petit discret int-dejavu-contexte">
              {p.site.nom}{p.equipement ? ` — ${p.equipement.libelle}` : ''}
              {p.equipement?.reference ? <> (<span className="mono">{p.equipement.reference}</span>)</> : null}
              {p.symptome ? ` — ${p.symptome}` : ''}
            </p>
            <div><LienBouton compact variante="fantome" to={p.lien_rapport} icone={<FileText aria-hidden />}>Rapport {p.reference}</LienBouton></div>
          </li>
        ))}
      </ul>
      {pannes.length > VISIBLES && (
        <div className="int-dejavu-plus">
          <Bouton compact variante="fantome" aria-expanded={tout} onClick={() => setTout(!tout)}>
            {tout ? 'Réduire' : `Voir ${pluriel(pannes.length - VISIBLES, 'autre panne', 'autres pannes')}`}</Bouton>
        </div>
      )}
    </>
  );
}

/** Le « Déjà vu » interroge la mémoire de l'entreprise côté serveur : hors connexion, il le dit au lieu d'un bloc vide. */
function HorsConnexion() {
  return (
    <p className="petit secondaire int-dejavu-ligne"><CloudOff aria-hidden size={18} />
      Le « Déjà vu » exige le réseau : il réapparaîtra au retour de la connexion.</p>
  );
}

export function PanneauDejaVu({ intervention: i, appliquer }: { intervention: Intervention; appliquer: (r: Intervention) => void }) {
  const { peut } = useSession();
  const notifier = useNotifier();
  const codes = (i.context?.codes ?? []).join(',');
  const q = useQuery({
    queryKey: ['int-deja-vu', i.id, codes, i.equipment_id],
    queryFn: () => api.get<ResultatDejaVu>(`/api/interventions/${i.id}/deja-vu`),
    retry: (n, e) => (e as ErreurApi)?.code !== 'hors_connexion' && n < 2,
  });
  const [occupe, setOccupe] = useState<string | null>(null);
  const ecrire = peut('intervention.ecrire') && i.modifiable;

  const reprendre = async (p: PanneResolue, causeId: string) => {
    setOccupe(`${p.id}:${causeId}`);
    try {
      const r = await api.post<{ intervention: Intervention }>(`/api/interventions/${i.id}/deja-vu/${p.id}/reprendre`, { causeId });
      appliquer(r.intervention);
      notifier('Hypothèse ajoutée : à vérifier sur cette installation.');
      q.refetch();
    } catch (e) { notifier(messageErreur(e), true); } finally { setOccupe(null); }
  };

  const horsConnexion = q.fetchStatus === 'paused' || (q.error as ErreurApi | null)?.code === 'hors_connexion';
  const pannes = q.data?.resultats ?? [];

  // Vide, hors connexion : une seule ligne discrète, pas de bloc décoratif.
  if (horsConnexion || (q.data && !pannes.length)) {
    return (
      <section className="panneau int-dejavu-discret int-o-dejavu" id="deja-vu" aria-label="Déjà vu">
        {horsConnexion ? <HorsConnexion /> : (
          <p className="petit secondaire int-dejavu-ligne"><History aria-hidden size={18} />
            <span><strong>Déjà vu</strong> — aucune panne résolue comparable pour l’instant.</span></p>
        )}
      </section>
    );
  }

  return (
    <Panneau titre="Déjà vu" id="deja-vu" className="int-o-dejavu"
      actions={pannes.length > 0 ? <span className="petit discret">{pluriel(pannes.length, 'panne résolue', 'pannes résolues')}</span> : undefined}>
      {q.isLoading && <Chargement lignes={2} libelle="Recherche des pannes déjà résolues…" />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {pannes.length > 0 && (
        <div className="pile" style={{ gap: 'var(--e-3)' }}>
          <p className="tres-petit discret">Pannes résolues dans l’entreprise, des plus proches aux plus lointaines. Une reprise reste une hypothèse à vérifier.</p>
          <ListePannesResolues pannes={pannes} reprendre={ecrire ? reprendre : undefined} occupe={occupe} />
        </div>
      )}
    </Panneau>
  );
}

/** Section de la fiche équipement (consultation seule ; le lien « Diagnostiquer » ouvre une intervention où reprendre). */
export function SectionPannesEquipement({ equipementId }: { equipementId: string }) {
  const q = useQuery({
    queryKey: ['equipement-pannes', equipementId],
    queryFn: () => api.get<ResultatDejaVu>(`/api/equipements/${equipementId}/pannes-resolues`),
    retry: (n, e) => (e as ErreurApi)?.code !== 'hors_connexion' && n < 2,
  });
  const horsConnexion = q.fetchStatus === 'paused' || (q.error as ErreurApi | null)?.code === 'hors_connexion';
  return (
    <Panneau titre="Pannes déjà résolues sur cet équipement et sa référence" id="pannes-resolues">
      <div className="pile" style={{ gap: 'var(--e-3)' }}>
        {q.isLoading && !horsConnexion && <Chargement lignes={2} />}
        {horsConnexion && <HorsConnexion />}
        {q.isError && !horsConnexion && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
        {q.data && q.data.resultats.length === 0 && (
          <p className="petit secondaire">Aucune panne résolue avec cause confirmée sur cet équipement{q.data.criteres.reference
            ? <> ni sur la référence <span className="mono">{q.data.criteres.reference}</span></> : ''} pour l’instant.</p>
        )}
        {q.data && q.data.resultats.length > 0 && <ListePannesResolues pannes={q.data.resultats} />}
        {q.data && (!q.data.criteres.reference || !q.data.criteres.fabricant) && (
          <p className="tres-petit discret">Sans fabricant et référence exacte, seules les pannes de cet équipement remontent.</p>
        )}
      </div>
    </Panneau>
  );
}
